import {AudienceRuleData, AudienceRuleFunction} from 'components/Audiences/types';
import React, {useEffect, useState} from 'react';
import {Space} from 'antd';
import {useIntl} from 'react-intl';
import {Select, Input, Departments, OrganizationalUnits, DatePicker} from 'components/Form';
import {config} from 'config/config';
import {connect} from 'react-redux';
import {useLocaleContext} from 'context/LocaleContext';
import helper from 'utils/generalHelpers';


const mapStateToProps = (state: any) => {
    return {
        session: state.session,
    }
}

const idBasedData: string[] = [
    AudienceRuleData.UserCustomer,
    AudienceRuleData.UserOrganizationalUnit,
    AudienceRuleData.Department,
];

// An unset function counts as id mode (the id widget is shown until a text function is picked).
const isIdFunction = (fn?: string): boolean =>
    !fn || fn === AudienceRuleFunction.EqualTo || fn === AudienceRuleFunction.NotEqualTo;

// Program "completed before / after" rules carry one date.
const isDateFunction = (fn?: string): boolean =>
    fn === AudienceRuleFunction.ProgramCompletedBefore || fn === AudienceRuleFunction.ProgramCompletedAfter;

const AudienceRuleFormItems: React.FC<any> = ({
                                                  audience,
                                                  customFields,
                                                  selectedCustomerId,
                                                  setSelectedCustomerId,
                                                  session,
                                                  ruleSetId,
                                                  fields,
                                                  form,
                                                  remove,
                                                  key,
                                                  name,
                                                  ...restField
                                              }) => {
    const [data, setData] = useState<string>();
    const [ruleFunction, setRuleFunction] = useState<string | undefined>();
    const [orgUnits, setOrgUnits] = useState<any[]|undefined>(undefined)
    const [departments, setDepartments] = useState<any[]|undefined>(undefined)
    const intl = useIntl();
    const {locale} = useLocaleContext();

    const [dataOptions, setDataOptions] = useState({
        [AudienceRuleData.UserPosition]: intl.formatMessage({id: 'general.position'}),
        [AudienceRuleData.UserCustomer]: intl.formatMessage({id: 'general.customer'}),
        [AudienceRuleData.UserOrganizationalUnit]: intl.formatMessage({id: 'general.unit'}),
        [AudienceRuleData.Department]: intl.formatMessage({id: 'general.department'}),
        [AudienceRuleData.CompletedCurriculum]: intl.formatMessage({id: 'general.learning_program'}),
    })

    useEffect(() => {
        if (customFields.length) {
            Object.values(customFields).map((el: any) => {
                setDataOptions(dataOptions => ({...dataOptions, [el.uuid]: el.title}));
            })
        }

    }, [customFields]);

    useEffect(() => {
        const currentRules = form.getFieldValue(['rulesGroup', ruleSetId, 'rules'])
        const groupValues = currentRules[restField.fieldKey];

        let newValue: number|undefined;

        // Text rules (contains / starts / ends / empty) hold a name, not an id.
        if (groupValues?.function && !isIdFunction(groupValues.function)) {
            return;
        }

        switch (groupValues?.data) {
            case 'DEPARTMENT':
                if (departments) {
                    if (departments.some(d => d.id == groupValues.value)) {
                        newValue = Number(groupValues.value)
                    } else {
                        newValue = 0
                    }
                }
                break

            case 'USER_ORGANIZATIONAL_UNIT':
                if (orgUnits) {
                    if (orgUnits.some(o => o.id == groupValues.value)) {
                        newValue = Number(groupValues.value)
                    } else {
                        newValue = 0
                    }
                }
                break

            default:
                break
        }

        if (newValue !== undefined) {
            currentRules[restField.fieldKey].value = newValue;

            form?.setFieldsValue({
                rulesGroup: {
                    [ruleSetId]: {
                        rules: [...currentRules]
                    }
                }
            });
        }

    }, [departments, orgUnits]);

    useEffect(() => {
        const groupValues = form.getFieldValue(['rulesGroup', ruleSetId, 'rules'])[restField.fieldKey];

        if (groupValues) {
            const {data: groupValueData, value} = groupValues;

            setData(groupValueData);
            setRuleFunction(groupValues.function);

            if (groupValueData === AudienceRuleData.UserCustomer && isIdFunction(groupValues.function)) {
                setSelectedCustomerId(value);
            }
        }

        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const functionOptions = {
        [AudienceRuleFunction.Contains]: intl.formatMessage({id: 'audience.contains'}),
        [AudienceRuleFunction.DoesNotContain]: intl.formatMessage({id: 'audience.does_not_contain'}),
        [AudienceRuleFunction.EqualTo]: intl.formatMessage({id: 'audience.equal_to'}),
        [AudienceRuleFunction.NotEqualTo]: intl.formatMessage({id: 'audience.not_equal_to'}),
        [AudienceRuleFunction.StartsWith]: intl.formatMessage({id: 'audience.starts_with'}),
        [AudienceRuleFunction.EndsWith]: intl.formatMessage({id: 'audience.ends_with'}),
        [AudienceRuleFunction.Before]: intl.formatMessage({id: 'audience.before'}),
        [AudienceRuleFunction.After]: intl.formatMessage({id: 'audience.after'}),
        [AudienceRuleFunction.Boolean]: intl.formatMessage({id: 'audience.boolean'}),
        [AudienceRuleFunction.Empty]: intl.formatMessage({id: 'audience.empty'}),
        [AudienceRuleFunction.ProgramAssigned]: intl.formatMessage({id: 'audience.program_assigned'}),
        [AudienceRuleFunction.ProgramCompleted]: intl.formatMessage({id: 'audience.program_completed'}),
        [AudienceRuleFunction.ProgramCompletedBefore]: intl.formatMessage({id: 'audience.program_completed_before'}),
        [AudienceRuleFunction.ProgramCompletedAfter]: intl.formatMessage({id: 'audience.program_completed_after'}),
    };

    const filterOperatorsFor = (dataKey?: string) => {
        if (dataKey) {
            return Object.fromEntries(Object.entries(functionOptions).filter(([key]) => {
                switch (dataKey) {
                    case AudienceRuleData.UserPosition:
                        return [
                            AudienceRuleFunction.Contains.toString(),
                            AudienceRuleFunction.DoesNotContain.toString(),
                            AudienceRuleFunction.EqualTo.toString(),
                            AudienceRuleFunction.NotEqualTo.toString(),
                            AudienceRuleFunction.Empty.toString(),
                            AudienceRuleFunction.StartsWith.toString(),
                            AudienceRuleFunction.EndsWith.toString(),
                        ].includes(key);
                    case AudienceRuleData.UserCustomer:
                    case AudienceRuleData.UserOrganizationalUnit:
                    case AudienceRuleData.Department:
                        return [
                            AudienceRuleFunction.EqualTo.toString(),
                            AudienceRuleFunction.NotEqualTo.toString(),
                            AudienceRuleFunction.Contains.toString(),
                            AudienceRuleFunction.StartsWith.toString(),
                            AudienceRuleFunction.EndsWith.toString(),
                        ].includes(key);
                    case AudienceRuleData.CompletedCurriculum:
                        return [
                            AudienceRuleFunction.ProgramAssigned.toString(),
                            AudienceRuleFunction.ProgramCompleted.toString(),
                            AudienceRuleFunction.ProgramCompletedBefore.toString(),
                            AudienceRuleFunction.ProgramCompletedAfter.toString(),
                        ].includes(key);
                    default:
                        return [
                            AudienceRuleFunction.Contains.toString(),
                            AudienceRuleFunction.DoesNotContain.toString(),
                            AudienceRuleFunction.EqualTo.toString(),
                            AudienceRuleFunction.NotEqualTo.toString(),
                            AudienceRuleFunction.Empty.toString(),
                            AudienceRuleFunction.StartsWith.toString(),
                            AudienceRuleFunction.EndsWith.toString(),
                        ].includes(key);
                }
            }));
        }

        return functionOptions;
    }

    const operators = filterOperatorsFor(data);

    const handleFunctionChange = (value?: string) => {
        const wasId = isIdFunction(ruleFunction);
        setRuleFunction(value);

        if (!isDateFunction(value)) {
            const rules = form.getFieldValue(['rulesGroup', ruleSetId, 'rules']);
            rules[restField.fieldKey].date = undefined;
            form?.setFieldsValue({rulesGroup: {[ruleSetId]: {rules: [...rules]}}});
        }

        // Switching between id and text mode (or to EMPTY) invalidates the stored value.
        if (idBasedData.includes(data as string) && (wasId !== isIdFunction(value) || value === AudienceRuleFunction.Empty)) {
            const currentRules = form.getFieldValue(['rulesGroup', ruleSetId, 'rules']);
            currentRules[restField.fieldKey].value = undefined;
            form?.setFieldsValue({rulesGroup: {[ruleSetId]: {rules: [...currentRules]}}});
        }
    };

    const isEmpty = ruleFunction === AudienceRuleFunction.Empty;
    const idMode = isIdFunction(ruleFunction);
    const idBased = idBasedData.includes(data as string);

    const handleDataChange = (value: any, options: any) => {
        const programRuleInvolved =
            data === AudienceRuleData.CompletedCurriculum ||
            value === AudienceRuleData.CompletedCurriculum;

        setData(value);
        findAndSetCustomerId();

        const currentRules = form.getFieldValue(['rulesGroup', ruleSetId, 'rules']);

        currentRules[restField.fieldKey].value = undefined;
        currentRules[restField.fieldKey].date = undefined;

        // Reset the function when the new data set does not allow it.
        const newOperators = Object.keys(filterOperatorsFor(value));
        if (!newOperators.includes(currentRules[restField.fieldKey].function)) {
            currentRules[restField.fieldKey].function = undefined;
            setRuleFunction(undefined);
        }

        // Program functions are only valid for the program data set (and vice versa).
        if (programRuleInvolved) {
            currentRules[restField.fieldKey].function = undefined;
        }

        form?.setFieldsValue({
            rulesGroup: {
                [ruleSetId]: {
                    rules: [...currentRules]
                }
            }
        });
    }

    const findAndSetCustomerId = () => {
        const addRulSetRules = form.getFieldValue(['rulesGroup', ruleSetId, 'rules']);

        const ruleSetCustomer = addRulSetRules.map((rule: any) => {
            if (rule.data === AudienceRuleData.UserCustomer && isIdFunction(rule.function)) {
                return rule.value;
            }

            return undefined;
        }).filter((element: any) => {
            return element !== undefined;
        })[0];

        setSelectedCustomerId(ruleSetCustomer);
    }

    function checkIfValidUUID(str?: string) {
        if (!str) {
            return false
        }
        const regexExp = /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/gi;

        return regexExp.test(str);
    }

    return (
        <Space key={key} className="audience-rule-set" align="baseline">
            <div>#{restField.fieldKey + 1}</div>
            <Space className="audience-rule-set-item">
                <Select customLayout={{wrapperCol: {span: 24}}}
                        name={[name, 'data']}
                        manualOptions={dataOptions}
                        onChange={(value: any, options: any) => handleDataChange(value, options)}
                        disabled={!audience.isEditable()}
                        {...restField}
                />

                <Select customLayout={{wrapperCol: {span: 24}}}
                        name={[name, 'function']}
                        manualOptions={operators}
                        onChange={(value: any) => handleFunctionChange(value)}
                        disabled={!audience.isEditable()}
                        {...restField}
                />

                {!isEmpty && (checkIfValidUUID(data) || (idBased && !idMode)) &&
                    <Input customLayout={{wrapperCol: {span: 24}}}
                           name={[name, 'value']}
                           disabled={!audience.isEditable()}
                           {...restField}
                    />}

                {!isEmpty && (!data || data === AudienceRuleData.UserPosition) &&
                    <Input customLayout={{wrapperCol: {span: 24}}}
                           name={[name, 'value']}
                           disabled={!audience.isEditable()}
                           {...restField}
                    />}

                {data === AudienceRuleData.UserCustomer && idMode && <Select
                    name={[name, 'value']}
                    allowClear
                    showSearch
                    dataKey='customers'
                    url={config.api.routes.backend.selectCustomersUuid}
                    customLayout={{wrapperCol: {span: 24}}}
                    dropdownStyle={{minWidth: "385px"}}
                    filterOption={(input: string, option: any) => {
                        return option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                    }}
                    onChange={(value: any, options: any) => setSelectedCustomerId(options.value)}
                    disabled={!audience.isEditable()}
                    {...restField}
                />}

                {data === AudienceRuleData.UserOrganizationalUnit && idMode && (
                    <OrganizationalUnits
                        name={[name, 'value']}
                        isUuid
                        setLoadedData={setOrgUnits}
                        hideLabel
                        canCreate={false}
                        customerId={selectedCustomerId}
                        customLayout={{wrapperCol: {span: 24}}}
                        dropdownStyle={{minWidth: "385px"}}
                        disabled={!audience.isEditable()}
                        {...restField}
                    />
                )}
                {data === AudienceRuleData.CompletedCurriculum && (
                    <Select
                        name={[name, 'value']}
                        showSearch
                        dataKey='programs'
                        url={'/api/v1/courses/learning-program/options/list'}
                        customObjLabel={(el: any) => helper.getLocaleTextString(el, locale)}
                        customLayout={{wrapperCol: {span: 24}}}
                        dropdownStyle={{minWidth: "385px"}}
                        filterOption={(input: string, option: any) =>
                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                        disabled={!audience.isEditable()}
                        {...restField}
                    />
                )}
                {data === AudienceRuleData.CompletedCurriculum && isDateFunction(ruleFunction) && (
                    <DatePicker
                        name={[name, 'date']}
                        format='DD.MM.YYYY'
                        customLayout={{wrapperCol: {span: 24}}}
                        disabled={!audience.isEditable()}
                    />
                )}
                {data === AudienceRuleData.Department && idMode && (
                    <Departments
                        name={[name, 'value']}
                        isUuid
                        setLoadedData={setDepartments}
                        hideLabel
                        canCreate={false}
                        customerId={selectedCustomerId}
                        customLayout={{wrapperCol: {span: 24}}}
                        dropdownStyle={{minWidth: "385px"}}
                        disabled={!audience.isEditable()}
                        {...restField}
                    />
                )}
            </Space>

            {audience.isEditable() ? <i onClick={() => remove(name)} style={{opacity: .5, cursor: 'pointer'}}
                                        className="fa-regular fa-trash"></i> : <div></div>}

        </Space>
    );
};

export default connect(mapStateToProps)(AudienceRuleFormItems);
